import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Grant {
  id: string;
  funder_program: string;
  program_focus: string | null;
  typical_grant_size: string | null;
  deadline_date: string | null;
  cycle_timeline: string | null;
  geography: string | null;
  eligibility_type: string | null;
  eligible_use_buckets: string[] | null;
  contact_name: string | null;
  contact_email: string | null;
  phone: string | null;
  application_info_url: string | null;
  notes: string | null;
  is_active: boolean;
  status: 'open' | 'closed';
  created_at: string;
  updated_at: string | null;
}

export interface UseGrantsOptions {
  userLocation?: string;
  userState?: string;
  limit?: number;
}

// Cache for grants data to avoid redundant API calls
const grantsCache = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

export const useGrants = (options: UseGrantsOptions = {}) => {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const realtimeChannelRef = useRef<any>(null);

  // Generate cache key based on options
  const getCacheKey = (opts: UseGrantsOptions) => {
    return `grants_${opts.userLocation || ''}_${opts.userState || ''}_${opts.limit || 'all'}`;
  };

  // Check if cached data is still valid
  const getCachedData = (cacheKey: string) => {
    const cached = grantsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
      return cached.data;
    }
    return null;
  };

  // Set up real-time updates for grants
  useEffect(() => {
    // Subscribe to real-time changes
    const channel = supabase
      .channel('grants-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'grants'
        },
        (payload) => {
          console.log('🔄 Grants real-time update:', payload);
          
          // Clear cache on any grants change
          grantsCache.clear();
          
          // Refresh grants data
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
            fetchGrants();
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, []);

  const fetchGrants = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching grants with options:', options);

      // Check cache first
      const cacheKey = getCacheKey(options);
      const cachedData = getCachedData(cacheKey);
      
      if (cachedData) {
        console.log('📋 Using cached grants data:', cachedData.length);
        setGrants(cachedData);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('grants')
        .select('*')
        .eq('is_active', true);

      if (options.limit) {
        query = query.limit(options.limit);
      }

      console.log('📡 Making grants database query...');
      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      let grantsData = (data || []) as Grant[];
      
      // Sort by status (open first), then by deadline
      grantsData = grantsData.sort((a, b) => {
        // First sort by status - "open" comes before "closed"
        const statusA = (a.status || '').toLowerCase();
        const statusB = (b.status || '').toLowerCase();
        
        if (statusA === 'open' && statusB !== 'open') return -1;
        if (statusA !== 'open' && statusB === 'open') return 1;
        
        // Then sort by deadline (closest first)
        if (a.deadline_date && b.deadline_date) {
          return new Date(a.deadline_date).getTime() - new Date(b.deadline_date).getTime();
        }
        if (a.deadline_date && !b.deadline_date) return -1;
        if (!a.deadline_date && b.deadline_date) return 1;
        
        return 0;
      });
      
      // Cache the data
      grantsCache.set(cacheKey, {
        data: grantsData,
        timestamp: Date.now()
      });

      console.log('✅ Fetched grants successfully:', grantsData.length);
      setGrants(grantsData);
    } catch (err: any) {
      console.error('❌ Error fetching grants:', err);
      setError(err.message || 'Failed to fetch grants');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced geolocation-based grant matching with caching
  const getNearbyGrants = useCallback(async (latitude: number, longitude: number) => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first for geolocation-based grants
      const geoCacheKey = `geo_grants_${latitude.toFixed(2)}_${longitude.toFixed(2)}_${options.limit || 'all'}`;
      const cachedGeoData = getCachedData(geoCacheKey);
      
      if (cachedGeoData) {
        console.log('📍 Using cached geo-grants data');
        setGrants(cachedGeoData);
        setLoading(false);
        return cachedGeoData;
      }

      // First, get user's state/region using reverse geocoding
      let userState = '';
      let userCountry = '';
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
        );
        const locationData = await response.json();
        
        if (locationData.address) {
          userState = locationData.address.state || locationData.address.province || '';
          userCountry = locationData.address.country || '';
          console.log('📍 User location details:', { userState, userCountry });
        }
      } catch (geoError) {
        console.warn('Reverse geocoding failed:', geoError);
      }

      // Fetch all active grants
      const { data: allGrants, error: fetchError } = await supabase
        .from('grants')
        .select('*, status')
        .eq('is_active', true);

      if (fetchError) {
        throw fetchError;
      }

      if (!allGrants || allGrants.length === 0) {
        setGrants([]);
        return [];
      }

      // Filter grants based on geography column
      const relevantGrants = allGrants.filter(grant => {
        if (!grant.geography) {
          return false; // Skip grants without geography information
        }

        const geography = grant.geography.toLowerCase();
        
        // Check for national grants
        if (geography.includes('national') || 
            geography.includes('united states') || 
            geography.includes('usa') ||
            geography.includes('nationwide')) {
          return true;
        }

        // Check for state-specific grants
        if (userState) {
          const stateVariations = [
            userState.toLowerCase(),
            userState.toLowerCase().replace(/\s+/g, ''),
            // Add common state abbreviations
            getStateAbbreviation(userState)
          ].filter(Boolean);

          if (stateVariations.some(variation => geography.includes(variation))) {
            return true;
          }
        }

        // Check for regional grants (e.g., "Southeast", "Midwest", etc.)
        if (userState) {
          const region = getRegionForState(userState);
          if (region && geography.includes(region.toLowerCase())) {
            return true;
          }
        }

        // Check for country-specific grants
        if (userCountry && geography.includes(userCountry.toLowerCase())) {
          return true;
        }

        return false;
      });

      // Sort by status (open first), then by deadline and relevance
      const sortedGrants = relevantGrants.sort((a, b) => {
        // First sort by status - "open" comes before "closed"
        const statusA = (a.status || '').toLowerCase();
        const statusB = (b.status || '').toLowerCase();
        
        if (statusA === 'open' && statusB !== 'open') return -1;
        if (statusA !== 'open' && statusB === 'open') return 1;
        
        // Then sort by deadline (closest first)
        if (a.deadline_date && b.deadline_date) {
          return new Date(a.deadline_date).getTime() - new Date(b.deadline_date).getTime();
        }
        if (a.deadline_date && !b.deadline_date) return -1;
        if (!a.deadline_date && b.deadline_date) return 1;
        
        // Finally by creation date (newer first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      console.log(`🎯 Found ${sortedGrants.length} relevant grants for location:`, {
        latitude,
        longitude,
        userState,
        userCountry,
        totalGrants: allGrants.length
      });

      const finalGrants = sortedGrants as Grant[];
      
      // Cache the geo-specific data
      grantsCache.set(geoCacheKey, {
        data: finalGrants,
        timestamp: Date.now()
      });

      setGrants(finalGrants);
      return finalGrants;
    } catch (err: any) {
      console.error('Error fetching nearby grants:', err);
      setError(err.message || 'Failed to fetch grants');
      return [];
    } finally {
      setLoading(false);
    }
  }, [options.limit]);

  // Helper function to get state abbreviation
  const getStateAbbreviation = (stateName: string): string => {
    const stateAbbreviations: { [key: string]: string } = {
      'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar', 'california': 'ca',
      'colorado': 'co', 'connecticut': 'ct', 'delaware': 'de', 'florida': 'fl', 'georgia': 'ga',
      'hawaii': 'hi', 'idaho': 'id', 'illinois': 'il', 'indiana': 'in', 'iowa': 'ia',
      'kansas': 'ks', 'kentucky': 'ky', 'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
      'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn', 'mississippi': 'ms', 
      'missouri': 'mo', 'montana': 'mt', 'nebraska': 'ne', 'nevada': 'nv', 'new hampshire': 'nh',
      'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny', 'north carolina': 'nc', 
      'north dakota': 'nd', 'ohio': 'oh', 'oklahoma': 'ok', 'oregon': 'or', 'pennsylvania': 'pa',
      'rhode island': 'ri', 'south carolina': 'sc', 'south dakota': 'sd', 'tennessee': 'tn',
      'texas': 'tx', 'utah': 'ut', 'vermont': 'vt', 'virginia': 'va', 'washington': 'wa',
      'west virginia': 'wv', 'wisconsin': 'wi', 'wyoming': 'wy'
    };
    return stateAbbreviations[stateName.toLowerCase()] || '';
  };

  // Helper function to get region for state
  const getRegionForState = (stateName: string): string => {
    const regions: { [key: string]: string } = {
      // Northeast
      'maine': 'northeast', 'new hampshire': 'northeast', 'vermont': 'northeast', 
      'massachusetts': 'northeast', 'rhode island': 'northeast', 'connecticut': 'northeast',
      'new york': 'northeast', 'new jersey': 'northeast', 'pennsylvania': 'northeast',
      
      // Southeast
      'delaware': 'southeast', 'maryland': 'southeast', 'virginia': 'southeast', 
      'west virginia': 'southeast', 'kentucky': 'southeast', 'tennessee': 'southeast',
      'north carolina': 'southeast', 'south carolina': 'southeast', 'georgia': 'southeast',
      'florida': 'southeast', 'alabama': 'southeast', 'mississippi': 'southeast',
      'arkansas': 'southeast', 'louisiana': 'southeast',
      
      // Midwest
      'ohio': 'midwest', 'michigan': 'midwest', 'indiana': 'midwest', 'wisconsin': 'midwest',
      'illinois': 'midwest', 'minnesota': 'midwest', 'iowa': 'midwest', 'missouri': 'midwest',
      'north dakota': 'midwest', 'south dakota': 'midwest', 'nebraska': 'midwest', 'kansas': 'midwest',
      
      // Southwest
      'oklahoma': 'southwest', 'texas': 'southwest', 'new mexico': 'southwest', 'arizona': 'southwest',
      
      // West
      'colorado': 'west', 'wyoming': 'west', 'montana': 'west', 'idaho': 'west',
      'washington': 'west', 'oregon': 'west', 'utah': 'west', 'nevada': 'west',
      'california': 'west', 'alaska': 'west', 'hawaii': 'west'
    };
    return regions[stateName.toLowerCase()] || '';
  };

  useEffect(() => {
    // Always fetch grants initially - geolocation-based fetching is handled separately by getNearbyGrants
    console.log('🚀 useGrants useEffect triggered, fetching grants...');
    fetchGrants();
  }, [options.userLocation, options.userState, options.limit]);

  return {
    grants,
    loading,
    error,
    refetch: fetchGrants,
    getNearbyGrants
  };
};